<template>
  <div v-if="!!label" :class="labelClasses" :id="getId('wrapper')">
    <div>
      <label>
        {{ label }}
      </label>
    </div>
    <div class="label-tooltip">
      <slot name="tooltip"> </slot>
    </div>
  </div>
  <div :id="getId('help-text')" class="-mt-2 mb-1 text-base" v-if="helpText || $slots.helpText">
    <slot name="helpText">
      {{ helpText }}
    </slot>
  </div>
  <slot
    name="default"
    v-bind="{
      errors,
      touched,
      dirty,
      valid,
      pending,
      errorMessage,
      resetField,
      setErrors,
      validate,
      handleChange,
      handleBlur,
      checked,
      validationState
    }"
  >
  </slot>
  <slot name="invalid-feedback" v-bind="{ errors }">
    <div class="invalid-feedback" v-if="!validationState && !suppressErrorMessages">
      <div class="feedback-icon">
        <HpIcon name="error" />
      </div>
      <div class="mt-0.5">{{ errors[0] }}</div>
    </div>
  </slot>
</template>

<script setup lang="ts">
  import type { PropType } from '@vue/runtime-core'

  const props = defineProps({
    id: {
      type: String,
      required: true
    },
    ...ValidationProvider.props,
    label: String,
    name: String,
    value: [String, Number, Boolean, Array, Object, Date, Symbol] as PropType<any>,
    helpText: String,
    suppressErrorMessages: {
      type: Boolean,
      default: false,
      required: false
    }
  })

  const { id, label, name, value: fieldValue, rules, validationMode } = toRefs(props)

  const { getId } = Id.use(id)

  const {
    value,
    errors,
    touched,
    dirty,
    valid,
    pending,
    errorMessage,
    resetField,
    setErrors,
    validate,
    handleChange,
    handleBlur,
    checked
  } = ValidationProvider.use(
    getId.value(),
    name?.value ?? label?.value ?? getId.value(),
    fieldValue,
    rules,
    validationMode.value
  )

  const validationState = computed(() => !errors.value?.[0])

  const labelClasses = computed(() => ({
    'form-label': true
  }))

  defineExpose({
    setErrors,
    errors,
    errorMessage,
    resetField,
    validate
  })
</script>

<style lang="postcss" scoped>
  .invalid-feedback {
    @apply mt-1.5 flex min-h-[1.25rem] items-start text-sm text-danger;

    & .feedback-icon {
      @apply mr-0.5;

      :deep(svg) {
        @apply scale-75 text-danger;
      }
    }
  }

  .form-label {
    @apply mb-1 flex min-h-[1.25rem] items-center font-semibold text-primary-900;

    & .label-tooltip {
      @apply -mt-3 h-6;

      :deep(svg) {
        @apply scale-75;
      }
    }
  }
</style>
